import { useCallback, useEffect, useState } from 'react';
import { Checkbox } from '@library/components/inputs';
import type { CheckboxOnChangeArg } from '@library/types/utils';
import { PLAYER_PLACEHOLDER_IMAGE_URL } from '@bo-constants/newWagers';
import type { PlayerData } from '@library/types/player';
import { formatDate } from '@library/component-utils/dateTime';

export interface PlayersTableRowProps {
  player: PlayerData;
  onClickPlayer: (playerId: string) => void;
  onClickCheckbox: ({ id, checked }: { id: string; checked: boolean }) => void;
  isAllChecked: boolean;
}

export const PlayersTableRow = ({
  player,
  onClickPlayer,
  onClickCheckbox,
  isAllChecked
}: PlayersTableRowProps) => {
  const {
    createdAt,
    id: playerId,
    imageUrl,
    leagueName,
    market,
    name,
    position,
    sportsradarId,
    team,
    teamName,
    updatedAt
  } = player;

  const [playerChecked, setPlayerChecked] = useState<boolean>(false);

  const handleOnClickPlayer = useCallback(() => {
    onClickPlayer(playerId);
  }, [onClickPlayer, playerId]);

  const handleCheckboxChange = useCallback(
    ({ id, checked }: CheckboxOnChangeArg) => {
      setPlayerChecked(checked);
      onClickCheckbox({ id, checked });
    },
    [onClickCheckbox, setPlayerChecked]
  );

  useEffect(() => {
    setPlayerChecked(isAllChecked);
  }, [isAllChecked]);

  return (
    <div className="border-soFresh-140 flex w-full items-center justify-start border-b text-start">
      <p className="w-[3%] px-3">
        <Checkbox
          id={playerId}
          checked={playerChecked}
          onCheckboxChange={handleCheckboxChange}
        />
      </p>
      <button
        className="border-soFresh-140 text-soClean-110 hover:bg-soFresh-120 text-2xs flex min-h-16 w-[97%] cursor-default items-center text-start xl:text-xs"
        data-testid={`player-${playerId}`}
        key={`player-${playerId}-row`}
        onClick={handleOnClickPlayer}
        type="button"
      >
        <p className="w-[10%] px-3" data-testid="player-id">
          {playerId}
        </p>
        <p
          className="flex w-[15%] justify-start px-3"
          data-testid="player-image"
        >
          <img
            src={imageUrl || PLAYER_PLACEHOLDER_IMAGE_URL}
            alt="Player"
            className="bg-soFresh-120 h-12 w-auto max-w-16 rounded-md placeholder:h-12 placeholder:w-auto"
          />
        </p>
        <p className="w-1/5 px-3" data-testid="player-name">
          {name}
        </p>
        <p className="w-[15%] px-3" data-testid="player-position">
          {position}
        </p>
        <p className="w-[15%] px-3" data-testid="player-team">
          {team}
        </p>
        <p className="w-[15%] px-3" data-testid="player-created-at">
          {formatDate({ date: createdAt })}
        </p>
        <p className="w-[15%] px-3" data-testid="player-updated-at">
          {formatDate({ date: updatedAt })}
        </p>
        <p
          className="w-[15%] overflow-hidden text-ellipsis px-3"
          data-testid="player-sportsradarId"
        >
          {sportsradarId || '-'}
        </p>
        <p className="w-[15%] px-3" data-testid="player-league">
          {leagueName || '-'}
        </p>
        <p className="w-[15%] px-3" data-testid="player-teamName">
          {teamName || '-'}
        </p>
        <p className="w-[15%] px-3" data-testid="player-market">
          {market || '-'}
        </p>
      </button>
    </div>
  );
};
